import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info, Building2, Quote } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const AZURE = "#1478B8";
const DEEP = "#0B3A57";
const NAME = "Orevillo's Dental Clinic";
const FB = "https://www.facebook.com/OrevillosDentalClinic/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function OrevillosConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Services", "#services"], ["Branches", "#branches"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#0F2A3A", fontFamily: "'Mulish', sans-serif" }} data-testid="orevillos-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Mulish:wght@400;500;600;700&display=swap'); .or-heading{font-family:'Poppins',sans-serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="or-logo" className="flex items-center gap-3">
            <img src="/dental/orevillos-dental-concept/logo.jpg" alt={NAME} className="h-11 object-contain" />
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#33566B] hover:text-[#1478B8] transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="or-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: AZURE }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="or-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — the real Evermall storefront */}
      <section className="relative min-h-[82vh] flex items-end" data-testid="or-hero">
        <img src="/dental/orevillos-dental-concept/storefront.jpg" alt="Orevillo's Dental Clinic storefront at Evermall, Dumaguete City" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "68% 30%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,58,87,.92) 0%, rgba(11,58,87,.72) 52%, rgba(11,58,87,.1) 86%), linear-gradient(0deg, rgba(11,58,87,.48), transparent 58%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 w-full">
          <motion.div {...fadeUp} className="max-w-2xl border-l-4 pl-5 sm:pl-7" style={{ borderColor: AZURE }}>
            <span className="rounded-full px-4 py-1.5 text-xs font-semibold" style={{ background: AZURE, color: "white" }}>Evermall, Dumaguete City</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl or-heading font-bold text-white leading-[1.1]">Serving Dumaguete &amp; Siquijor Since 2008</h1>
            <p className="mt-5 text-lg text-white/80 max-w-xl">General and cosmetic dentistry, including veneers and crowns, from our Evermall clinic.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#visit" data-testid="or-hero-book" className="px-8 py-3.5 text-sm font-semibold text-white" style={{ background: AZURE }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="or-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl or-heading font-semibold leading-relaxed" style={{ color: DEEP }}>
          Orevillo's Dental Clinic has served Dumaguete City and Siquijor since 2008, offering general and cosmetic dentistry.
        </motion.p>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20" style={{ background: "#EAF3FA" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="or-heading text-3xl font-semibold text-center mb-12" style={{ color: DEEP }}>Our Services</motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {["General Dentistry", "Cosmetic Dentistry (Veneers & Crowns)", "Tooth Extraction", "Consultations"].map((s) => (
              <motion.div key={s} {...fadeUp} className="py-6 px-6 bg-white rounded-xl shadow-sm border-l-4" style={{ borderColor: AZURE }}>
                <p className="font-semibold" style={{ color: DEEP }}>{s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANCHES */}
      <section id="branches" className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="or-heading text-3xl font-semibold text-center mb-12" style={{ color: DEEP }}>Two Places to Find Us</motion.h2>
        <div className="grid sm:grid-cols-2 gap-8">
          {[
            { name: "Evermall Clinic", note: "Ground Floor, Evermall, Dumaguete City" },
            { name: "Siquijor Branch", note: "Serving Siquijor — walk-ins and appointments" },
          ].map((b) => (
            <motion.div key={b.name} {...fadeUp} data-testid="or-branch" className="p-8 rounded-xl shadow-sm" style={{ background: "#EAF3FA" }}>
              <Building2 size={22} style={{ color: AZURE }} />
              <h3 className="mt-4 or-heading text-xl font-semibold" style={{ color: DEEP }}>{b.name}</h3>
              <p className="mt-2 text-[#33566B]/80">{b.note}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* REVIEW */}
      <section id="reviews" className="py-20" style={{ background: DEEP }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="or-heading text-3xl text-white text-center mb-12">What Patients Reported</motion.h2>
          <div className="grid md:grid-cols-3 gap-7">
            {[
              { text: "A filling done by the clinic was later praised by a US dentist and avoided a crown.", author: "Stephen Keierleber", source: "ClinicFinderPH", sourceUrl: "https://www.clinicfinderph.com/clinic/orevillos-dental-clinic" },
              { text: "Strongly dissatisfied with the treatment and service received.", author: "Sarah Aviso", source: "ClinicFinderPH", sourceUrl: "https://www.clinicfinderph.com/clinic/orevillos-dental-clinic" },
              { text: "The clinic was not the cheapest, but seemed well equipped; a child had teeth pulled without tears.", author: "Rhoody", source: "DumagueteInfo forum" },
            ].map((review, index) => (
              <motion.div key={index} {...fadeUp} className="rounded-xl border border-white/10 bg-white/5 p-7 text-center">
                <Quote size={25} className="mx-auto mb-4" style={{ color: AZURE }} />
                <p className="text-base text-white/85 leading-relaxed">"{review.text}"</p>
                <p className="mt-5 text-sm text-white/50">— {review.author} · {review.sourceUrl ? <a href={review.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{review.source}</a> : review.source}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="or-heading text-3xl font-semibold" style={{ color: DEEP }}>Visit Us</h2>
          <ul className="mt-7 space-y-5">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: AZURE }} className="mt-0.5" />Ground Floor, Evermall, Dumaguete City</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: AZURE }} className="mt-0.5" /><a href="tel:+639498812024" className="hover:underline">0949 881 2024</a> · (035) 422-4272</li>
            <li className="flex items-start gap-4"><Mail size={20} style={{ color: AZURE }} className="mt-0.5" /><a href="mailto:orevillodental08@yahoo.com" className="hover:underline">orevillodental08@yahoo.com</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: AZURE }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="or-facebook-link" className="hover:underline">Orevillo's Dental Clinic</a></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[320px] rounded-xl overflow-hidden shadow-md">
          <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Evermall Dumaguete City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </motion.div>
      </section>

      <footer className="py-10 bg-white border-t" style={{ borderColor: "#E0EBF3" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#5A7286]">
          <p className="or-heading text-base font-semibold" style={{ color: DEEP }}>Orevillo's Dental Clinic</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: AZURE }}>Message Orevillo's</a>
      <ConceptCTAPill slug="orevillos-dental-concept" />
    </div>
  );
}
